import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import TableLayout from "../../layouts/TableLayout/NewTableLayout";
import { getTableConfig } from "./table.config";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import {
  useDeletePayoutsMutation,
  useGetPayoutsQuery,
  useLazyGetPayoutQuery,
  useUpdatePayoutMutation,
} from "../../redux/services/payouts/payoutsApi";
import { renderCustomColumnDefs } from "../../shared/Table/table.utils";
import PayoutLayout from "../../layouts/PayoutLayout";
import { createRoot } from "react-dom/client";
import CreateModal from "./CreateModal";
import { useConfig } from "../../hooks";
import { useFetchAll } from "../../hooks/useFetchAll";
import { getPdfConfig } from "./Show/table.configs";
import { EmployeesFilterList, StatusFilterList } from "../../components";
import { isAuthorized } from "../../utils/authority";

const PdfAutoDownloadTrigger = ({
  download,
  handleDownloadPdf,
  onComplete,
}) => {
  useEffect(() => {
    if (!download || typeof handleDownloadPdf !== "function") return;

    let cancelled = false;
    (async () => {
      try {
        await handleDownloadPdf(false);
      } finally {
        if (!cancelled) onComplete?.();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [download, handleDownloadPdf, onComplete]);

  return null;
};

const Payouts = ({ employeeId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { appType } = useConfig();
  const { COLUMN_DEFINITIONS } = getTableConfig(appType);
  const { pdfConfig = { COLUMN_DEFINITIONS: [] } } = useMemo(
    () => getPdfConfig(appType) || {},
    [appType]
  );
  const pdfColumnDefs = pdfConfig?.COLUMN_DEFINITIONS || [];

  const [triggerGetPayout] = useLazyGetPayoutQuery();
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Define filter types similar to Jobs
  const filterTypes = useMemo(
    () =>
      [
        {
          id: "payDate",
          type: "date",
          label: "Pay Date",
          defaultText: "All Time",
          defaultValue: {
            id: "allTime",
            value: null,
          },
        },
        {
          id: "status",
          type: "multiple",
          label: "Status",
          defaultText: "All",
          content: StatusFilterList,
        },
        {
          id: "employeeId",
          remove: !!employeeId, // Remove if employeeId prop is provided
          type: "multiple",
          label: "Employee",
          defaultText: "All Employees",
          content: EmployeesFilterList,
        },
      ].filter((i) => !i.remove),
    [employeeId]
  );

  // Use useFetchAll hook
  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch },
    filters: { filters, setFilters },
    queryResult: { data, isLoading, isFetching },
  } = useFetchAll({
    queryHook: useGetPayoutsQuery,
    defaultPageSize: 20,
    defaultFilters: [{ id: "employeeId", value: employeeId }],
  });
  const [updatePayout] = useUpdatePayoutMutation();
  const [deletePayouts] = useDeletePayoutsMutation();

  useEffect(() => {
    if (employeeId) {
      setFilters((prev) => [...prev, { id: "employeeId", value: employeeId }]);
    }
  }, [employeeId, setFilters]);

  const downloadPayoutPdf = useCallback(
    async ({ payout }) => {
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "-9999px";
      document.body.appendChild(container);

      const root = createRoot(container);

      return new Promise((resolve, reject) => {
        let finished = false;

        const cleanup = () => {
          try {
            root.unmount();
          } catch {
            // ignore
          }
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        };

        const handleComplete = () => {
          if (finished) return;
          finished = true;
          setTimeout(() => {
            cleanup();
            resolve();
          }, 100);
        };

        try {
          root.render(
            <PayoutLayout
              payout={payout}
              columns={pdfColumnDefs}
              appType={appType}
              download={true}
            >
              <PdfAutoDownloadTrigger
                download={true}
                onComplete={handleComplete}
              />
            </PayoutLayout>
          );
        } catch (e) {
          finished = true;
          cleanup();
          reject(e);
        }

        // Prevent hanging forever
        setTimeout(() => {
          if (finished) return;
          finished = true;
          cleanup();
          reject(new Error("PDF generation timeout"));
        }, 15000);
      });
    },
    [appType, pdfColumnDefs]
  );

  const handleDownloadPDF = useCallback(
    async (selectedItems) => {
      const items = Array.isArray(selectedItems)
        ? selectedItems
        : selectedItems
        ? [selectedItems]
        : [];

      if (!items.length) {
        toast.info("Select at least one payout to download.", {
          toastId: "payouts-download-pdf-none-selected",
        });
        return;
      }

      for (const item of items) {
        try {
          const id = item?._id;
          const payout = id ? await triggerGetPayout({ id }).unwrap() : item;

          await downloadPayoutPdf({ payout });
        } catch (error) {
          console.error("Download failed:", error);
          toast.error("Failed to generate PDF", {
            toastId: "payouts-download-pdf-failed",
          });
        }
      }
    },
    [downloadPayoutPdf, triggerGetPayout]
  );

  const newTableData = [
    ...COLUMN_DEFINITIONS,
    {
      id: "download",
      header: "PDF",
      accessorKey: "download",
      cell: ({ row }) => {
        return (
          <button
            type="button"
            className="flex justify-center w-full cursor-pointer hover:text-primary-600"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadPDF(row.original);
            }}
            title="Download PDF"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
          </button>
        );
      },
    },
  ];

  const NEW_COLUMN_DEFINITIONS = renderCustomColumnDefs(newTableData);

  function handleEdit(itemId) {
    navigate(`/payouts/${itemId}`);
  }

  async function handleDelete(items) {
    const res = await deletePayouts({ data: items.map((item) => item._id) });

    toast(res.data.message, {
      type: res.data.status,
      toastId: "delete-payouts",
    });

    return res.data;
  }

  async function handleUpdate(e, setLoading) {
    try {
      const updateData = {
        id: e.row.original._id,
        data: e.data,
      };

      const res = await updatePayout(updateData).unwrap();

      toast(res.message, {
        type: res.status,
        toastId: "index-delete-payouts",
      });
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }

  const cannotCreateOrEdit =
    user.authority === "client" || user.authority === "user";

  return (
    <>
      <TableLayout
        id="payouts"
        loading={isLoading || isFetching}
        columns={NEW_COLUMN_DEFINITIONS}
        data={data?.payouts}
        pagination={data}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPaginationChange={setPagination}
        handleDownloadPDF={handleDownloadPDF}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        onUpdate={handleUpdate}
        mainAction={{ onClick: () => setCreateModalOpen(true) }}
        hideActions={cannotCreateOrEdit}
        hideMainAction={cannotCreateOrEdit}
        fullTextSearch={handleGlobalSearch}
        globalSearchFilters={["payoutId"]}
        filters={filterTypes}
        onFiltersChange={setFilters}
        meta={{
          updateData: handleUpdate,
        }}
      />
      <CreateModal
        modalOpen={createModalOpen}
        setModalOpen={setCreateModalOpen}
      />
    </>
  );
};

export default Payouts;
